import React, { useState, useMemo, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAppSelector } from "../store";
import { Package, Settings, ShoppingCart, LogOut, ChevronDown, User, ChevronRight, UserCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLogout } from "../Hooks/useAuth";

/* ===================== Roles & Access Rules ===================== */

const ROLES = {
  AHLAM: "ahlam",
  DEPO: "depo",
  SENIOR: "senior",
  PRODUCTION: "production",
};

// قاعدتك: production (فقط معاملات المخزن) | senior & ahlam (الكل) | depo (كل المخزن + أسماء المنتجات)
const canSeeItem = (role, sectionId, itemId) => {
  if (!role) return false;

  if (role === ROLES.AHLAM || role === ROLES.SENIOR) {
    return true; // كل الصفحات
  }

  if (role === ROLES.PRODUCTION) {
    return itemId === "depo-transactions"; // فقط معاملات المخزن
  }

  if (role === ROLES.DEPO) {
    const depoItems = new Set(["packaging", "raw", "ready", "depo-transactions"]);
    if (sectionId === "depo") return depoItems.has(itemId);
    if (sectionId === "C") return ["product-names", "raw-material-names", "ready-material-names"].includes(itemId); // أسماء المنتجات
    return false;
  }

  return false;
};

/* ===================== Menu Model (UI text via i18n) ===================== */

const buildMenu = (t) => [
  {
    id: "depo",
    label: t("sidebar.depo"),
    icon: Package,
    items: [
      { id: "packaging", label: t("sidebar.packaging-materials"), path: "/packaging-materials" },
      { id: "raw",       label: t("sidebar.raw-materials"),       path: "/raw-materials" },
      { id: "ready",     label: t("sidebar.ready"),                path: "/ready-materials" },
      { id: "depo-transactions", label: t("sidebar.depo-transactions") || "معاملات المخزن", path: "/depo-transactions" },
    ],
  },
  {
    id: "orders",
    label: t("sidebar.orders"),
    icon: ShoppingCart,
    items: [
      { id: "orders-list", label: t("sidebar.orders-list"), path: "/orders" },
    ],
  },
  {
    id: "C",
    label: t("sidebar.settings"),
    icon: Settings,
    items: [
      { id: "product-names", label: t("sidebar.product-names") || "أسماء مواد التغليف", path: "/product-names" },
      { id: "raw-material-names", label: t("sidebar.raw-material-names") || "أسماء المواد الخام", path: "/raw-material-names" },
      { id: "ready-material-names", label: t("sidebar.ready-material-names") || "أسماء المواد الجاهزة", path: "/ready-material-names" },
      { id: "users",         label: t("sidebar.users") || "المستخدمين",             path: "/users" },
    ],
  },
];

/* ===================== UI Atoms ===================== */

const SectionHeader = ({ section, isOpen, collapsed, onClick }) => {
  const Icon = section.icon;
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-sm rounded-lg transition-all duration-300 ease-in-out ${
        isOpen
          ? "bg-[var(--sidebar-accent)] text-[var(--sidebar-accent-foreground)]"
          : "text-[var(--sidebar-accent-foreground)] hover:bg-[var(--sidebar-accent)] hover:text-[var(--sidebar-accent-foreground)]"
      }`}
    >
      <div className="flex items-center gap-3">
        <Icon className="w-4 h-4 flex-shrink-0" />
        {!collapsed && <span>{section.label}</span>}
      </div>
      {!collapsed && (
        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
      )}
    </button>
  );
};

const MenuItem = ({ item, isActive }) => {
  return (
    <Link
      to={item.path}
      className={`block px-3 py-1.5 text-sm rounded-lg transition-all duration-200 ease-in-out ${
        isActive
          ? "bg-[var(--sidebar-accent)] text-[var(--sidebar-accent-foreground)]"
          : "text-[var(--sidebar-accent-foreground)] hover:bg-[var(--sidebar-accent)] hover:text-[var(--sidebar-accent-foreground)]"
      }`}
    >
      {item.label}
    </Link>
  );
};

/* ===================== Sidebar ===================== */

const Sidebar = ({ currentUser }) => {
  const location = useLocation();
  const collapsed = useAppSelector((s) => s.ui.sidebarCollapsed);
  const [openDropdown, setOpenDropdown] = useState("depo");
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const { t } = useTranslation();
  const { mutate: logout, isPending: isLoggingOut } = useLogout();

  const role = currentUser?.role;

  // بناء القائمة (labels من i18n)
  const fullMenu = useMemo(() => buildMenu(t), [t]);

  // فلترة القائمة حسب صلاحيات الدور
  const filteredMenu = useMemo(() => {
    return fullMenu
      .map((section) => {
        const visibleItems = section.items.filter((it) =>
          canSeeItem(role, section.id, it.id)
        );
        return { ...section, items: visibleItems };
      })
      .filter((section) => section.items.length > 0); // إخفاء الأقسام الفارغة
  }, [fullMenu, role]);

  // فتح/إغلاق الأقسام
  const toggleDropdown = useCallback(
    (sectionId) => setOpenDropdown((prev) => (prev === sectionId ? null : sectionId)),
    []
  );

  const openDropdowns = { [openDropdown]: true };

  return (
    <aside
      className={`bg-[var(--sidebar)] border-l border-[var(--sidebar-border)] transition-all duration-300 ease-in-out ${
        collapsed ? "w-16" : "w-56"
      } flex flex-col`}
    >
      {/* القائمة الرئيسية مع الأنيميشن */}
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        {filteredMenu.map((section, index) => (
          <div
            key={section.id}
            className={`transition-all duration-700 ease-in-out ${
              collapsed ? "translate-x-4 opacity-0" : "translate-x-0 opacity-100"
            }`}
            style={{ transitionDelay: collapsed ? "0ms" : `${index * 100}ms` }}
          >
            {/* رأس القسم */}
            <SectionHeader
              section={section}
              isOpen={openDropdowns[section.id]}
              collapsed={collapsed}
              onClick={() => toggleDropdown(section.id)}
            />

            {/* العناصر الفرعية مع أنيميشن */}
            <div
              className={`overflow-hidden transition-all duration-500 ease-in-out ${
                openDropdowns[section.id] && !collapsed
                  ? "max-h-[500px] opacity-100 mt-2"
                  : "max-h-0 opacity-0 mt-0"
              }`}
            >
              <div className="mr-4 space-y-1">
                {section.items.map((item, itemIndex) => (
                  <div
                    key={item.id}
                    className={`transition-all duration-300 ease-out ${
                      openDropdowns[section.id] && !collapsed
                        ? "translate-y-0 opacity-100"
                        : "translate-y-2 opacity-0"
                    }`}
                    style={{
                      transitionDelay:
                        openDropdowns[section.id] && !collapsed ? `${itemIndex * 50}ms` : "0ms",
                    }}
                  >
                    <MenuItem item={item} isActive={location.pathname === item.path} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </nav>

      {/* قسم الحساب في أسفل الـ Sidebar */}
      <div className="p-4 border-t border-[var(--sidebar-border)]">
        {!collapsed && (
          <div className="relative">
            <button
              onClick={() => setShowAccountMenu(!showAccountMenu)}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-all duration-300 ease-in-out transform text-[var(--sidebar-accent-foreground)] hover:bg-[var(--sidebar-accent)] hover:text-[var(--sidebar-accent-foreground)] ${
                showAccountMenu ? 'bg-[var(--sidebar-accent)] text-[var(--sidebar-accent-foreground)]' : ''
              }`}
            >
              <User className="w-4 h-4 flex-shrink-0 transition-transform duration-200" />
              <div className="flex-1 min-w-0 text-right">
                <p className="text-sm font-medium truncate">{currentUser?.name || 'المستخدم'}</p>
                <p className="text-xs opacity-70 truncate" dir="ltr" style={{ textAlign: 'left' }}>{currentUser?.email || ''}</p>
              </div>
              <div className="flex-shrink-0">
                {showAccountMenu ? (
                  <ChevronDown className="w-4 h-4 transition-transform duration-200" />
                ) : (
                  <ChevronRight className="w-4 h-4 transition-transform duration-200" />
                )}
              </div>
            </button>

            {/* القائمة المنبثقة */}
            {showAccountMenu && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-[var(--sidebar)] border border-[var(--sidebar-border)] rounded-lg shadow-lg z-50">
                <div className="p-2 space-y-1">
                  <Link
                    to="/profile"
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-all duration-300 ease-in-out transform text-[var(--sidebar-accent-foreground)] hover:bg-[var(--sidebar-accent)] hover:text-[var(--sidebar-accent-foreground)]"
                    onClick={() => setShowAccountMenu(false)}
                  >
                    <UserCircle className="w-4 h-4 flex-shrink-0 transition-transform duration-200" />
                    <span className="truncate text-right">{t('sidebar.profile') || 'الملف الشخصي'}</span>
                  </Link>
                  
                  {/* فاصل */}
                  <div className="border-t border-[var(--sidebar-border)] my-1"></div>
                  
                  {/* زر تسجيل الخروج */}
                  <button
                    onClick={() => {
                      setShowAccountMenu(false);
                      logout();
                    }}
                    disabled={isLoggingOut}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-all duration-300 ease-in-out transform text-red-400 hover:bg-red-500 hover:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <LogOut className="w-4 h-4 flex-shrink-0 transition-transform duration-200" />
                    <span className="truncate text-right">{isLoggingOut ? (t('sidebar.loggingOut') || 'جاري تسجيل الخروج...') : (t('sidebar.logout') || 'تسجيل الخروج')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
